import api from './api';

// Push Notification Service
class PushNotificationService {
  constructor() {
    this.registration = null;
    this.permission = null;
    this.vapidPublicKey = null;
    this.lastInitErrorCode = null;
    this.lastInitError = null;
  }

  setLastInitError(code, message) {
    this.lastInitErrorCode = code || null;
    this.lastInitError = message || null;
  }

  getLastInitError() {
    return this.lastInitError;
  }

  getLastInitErrorCode() {
    return this.lastInitErrorCode;
  }

  buildError(code, debugMessage = null) {
    return {
      success: false,
      errorCode: code,
      error: `Error ${code}`,
      debugMessage
    };
  }

  async initialize() {
    if (!('Notification' in window)) {
      this.setLastInitError(101, 'This browser does not support push notifications');
      return false;
    }

    if (!('serviceWorker' in navigator)) {
      this.setLastInitError(102, 'Service workers are unavailable in this browser');
      return false;
    }

    const isLocalhost = ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);
    if (!window.isSecureContext && !isLocalhost) {
      this.setLastInitError(103, 'Push notifications require a secure HTTPS connection');
      return false;
    }

    try {
      // Get service worker registration
      this.registration = await navigator.serviceWorker.ready;
      if (!this.registration || !this.registration.pushManager) {
        this.setLastInitError(104, 'Push manager is unavailable for this service worker');
        return false;
      }
      
      // Check current permission
      this.permission = Notification.permission;
      
      // Request permission if not already granted/denied
      if (this.permission === 'default') {
        try {
          this.permission = await Notification.requestPermission();
        } catch (permissionError) {
          this.setLastInitError(105, `Could not request notification permission: ${permissionError?.message || 'Unknown error'}`);
          return false;
        }
      }
      
      if (this.permission === 'granted') {
        this.setLastInitError(null, null);
        return true;
      }

      if (this.permission === 'denied') {
        this.setLastInitError(106, 'Notification permission is denied in browser settings');
        return false;
      }

      this.setLastInitError(107, 'Notification permission was not granted');
      return false;
    } catch (error) {
      this.setLastInitError(108, `Failed to initialise push notifications: ${error?.message || 'Unknown error'}`);
      return false;
    }
  }

  async requestPermission() {
    if (!('Notification' in window)) {
      return false;
    }

    if (Notification.permission === 'granted') {
      return true;
    }

    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }

    return false;
  }

  urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }

    return outputArray;
  }

  async getExistingSubscription() {
    if (!this.registration || !this.registration.pushManager) return null;
    return this.registration.pushManager.getSubscription();
  }

  async subscribeWithServer() {
    if (!this.registration || !this.registration.pushManager) {
      return this.buildError(201, 'Service worker or push manager not available');
    }

    if (Notification.permission !== 'granted') {
      return this.buildError(202, 'Notification permission not granted');
    }

    try {
      const keyResponse = await api.get('/api/push/vapid-public-key');
      this.vapidPublicKey = keyResponse.data?.publicKey;
      if (!this.vapidPublicKey) {
        return this.buildError(203, 'Push notifications not configured on server');
      }

      let subscription = await this.registration.pushManager.getSubscription();
      if (!subscription) {
        subscription = await this.registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: this.urlBase64ToUint8Array(this.vapidPublicKey)
        });
      }

      await api.post('/api/push/subscribe', {
        subscription: subscription.toJSON()
      });

      return { success: true };
    } catch (error) {
      const status = error?.response?.status;
      const code = status === 400 ? 204 : status === 401 ? 205 : status === 503 ? 206 : 207;
      const msg = error?.response?.data?.message || error?.message || 'Subscription failed';
      return this.buildError(code, msg);
    }
  }

  async unsubscribeFromServer() {
    try {
      const subscription = await this.getExistingSubscription();
      if (subscription) {
        await api.delete('/api/push/subscribe', {
          data: { endpoint: subscription.endpoint }
        });
        await subscription.unsubscribe();
      } else {
        await api.delete('/api/push/subscribe');
      }
      return true;
    } catch (error) {
      return false;
    }
  }

  async syncPreference(enabled) {
    if (!enabled) {
      await this.unsubscribeFromServer();
      return { success: true };
    }

    const initialised = await this.initialize();
    if (!initialised) {
      return this.buildError(
        this.getLastInitErrorCode() || 108,
        this.getLastInitError() || 'Could not initialise notifications'
      );
    }

    return this.subscribeWithServer();
  }

  showNotification(title, options = {}) {
    if (!('Notification' in window)) {
      return;
    }

    if (Notification.permission !== 'granted') {
      // Try to request permission again
      this.requestPermission().then(granted => {
        if (granted) {
          this.showNotification(title, options);
        }
      });
      return;
    }

    const notificationOptions = {
      body: options.body || '',
      icon: options.icon || '/img/favicon.png',
      badge: options.badge || '/img/favicon.png',
      tag: options.tag || 'default',
      requireInteraction: options.requireInteraction || false,
      data: options.data || {},
      ...options
    };

    try {
      // Try service worker first, fallback to direct notification
      if (this.registration && this.registration.showNotification) {
        this.registration.showNotification(title, notificationOptions)
          .then(() => {})
          .catch(() => {
          // Fallback to direct notification
          new Notification(title, notificationOptions);
        });
      } else {
        const notification = new Notification(title, notificationOptions);

        // Handle click
        notification.onclick = (event) => {
          event.preventDefault();
          if (options.data && options.data.url) {
            window.focus();
            window.location.href = options.data.url;
          } else if (options.data && options.data.chatId) {
            window.focus();
            window.location.href = `/chat/${options.data.chatId}`;
          }
          notification.close();
        };
      }
    } catch (error) {}
  }

  showChatNotification(chatTitle, message, chatId) {
    this.showNotification(`New message from ${chatTitle}`, {
      body: message.length > 100 ? message.substring(0, 100) + '...' : message,
      icon: '/img/favicon.png',
      tag: `chat-${chatId}`,
      requireInteraction: false,
      data: {
        chatId: chatId,
        type: 'chat'
      }
    });
  }

  closeNotification(tag) {
    if (this.registration) {
      this.registration.getNotifications({ tag }).then(notifications => {
        notifications.forEach(notification => notification.close());
      });
    }
  }
}

const pushNotificationService = new PushNotificationService();

export default pushNotificationService;

